import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Button, IconWarning, Link, Checkbox,
} from 'gatorcomponents';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as Styles from './VpsStoreModal.styles';
import useLocale from '@/hooks/useLocale';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

const VpsStoreModal = ({
  modalType,
  modalConfig,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { id: hostingId } = useParams();
  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: localeVpsOci } = productDetailsLocale;
  const { modal: localeRebuildModal } = localeVpsOci && localeVpsOci.store && localeVpsOci.store.rebuild;

  const vpsOci = useSelector(state => state.vpsOci);
  const rebuildIsLoading = vpsOci && vpsOci.rebuild ? vpsOci.rebuild.loading : false;

  const [checkedTerms, setCheckedTerms] = useState([]);

  const handleCloseModal = useCallback(() => {
    onClose && onClose();
  }, [onClose]);

  const handleRebuild = useCallback(() => {
    if (hostingId) {
      dispatch(vpsOciActions.rebuild.request(hostingId));
    }
  }, [dispatch, hostingId]);

  const modalConfigComponent = {
    ...modalConfig,
    serverInactive: {
      title: localeRebuildModal.serverInactive.title,
      message: localeRebuildModal.serverInactive.message,
      testId: 'vpsoci-rebuild-server-inactive-modal',
      confirmButton: {
        text: localeRebuildModal.serverInactive.confirmButton,
        action: handleCloseModal,
        testId: 'vpsoci-rebuild-server-inactive-modal-close-button',
      },
    },
    limitExceeded: {
      title: localeRebuildModal.limitExceeded.title,
      message: localeRebuildModal.limitExceeded.message,
      testId: 'vpsoci-rebuild-limit-exceeded-modal',
      confirmButton: {
        text: localeRebuildModal.limitExceeded.confirmButton,
        action: handleCloseModal,
        testId: 'vpsoci-rebuild-limit-exceeded-modal-close-button',
      },
    },
    notFound: {
      title: localeRebuildModal.notFound.title,
      message: localeRebuildModal.notFound.message,
      testId: 'vpsoci-rebuild-not-found-modal',
      confirmButton: {
        text: localeRebuildModal.notFound.confirmButton,
        action: handleCloseModal,
        testId: 'vpsoci-rebuild-not-found-modal-close-button',
      },
    },
    unknownError: {
      title: localeRebuildModal.unknownError.title,
      message: localeRebuildModal.unknownError.message,
      testId: 'vpsoci-rebuild-unknown-error-modal',
      closeButton: {
        text: localeRebuildModal.unknownError.closeButton,
        action: handleCloseModal,
        testId: 'vpsoci-rebuild-unknown-error-modal-close-button',
      },
      confirmButton: {
        text: localeRebuildModal.unknownError.confirmButton,
        action: handleRebuild,
        loading: rebuildIsLoading,
        testId: 'vpsoci-rebuild-unknown-error-modal-try-again-button',
      },
    },
  };

  const config = ((modalConfigComponent && modalType) && modalConfigComponent[modalType]) || null;

  const resetCheckedTerms = () => {
    setCheckedTerms([]);
  };

  useEffect(() => {
    resetCheckedTerms();
  }, [modalType]);

  const handleCheckboxChange = (termId) => {
    setCheckedTerms(prev => (prev.includes(termId)
      ? prev.filter(id => id !== termId)
      : [...prev, termId]));
  };

  if (!config) return null;
  const allTermsAccepted = config.agreedTerms && config.agreedTerms.length === checkedTerms.length;

  return (
    <Modal
      onClose={onClose}
      onGoBack={null}
      title={config.title}
      testId={config.testId}
      maxWidth={800}
    >
      <Styles.ModalContent>{config.message}</Styles.ModalContent>
      {config.alert && (
        <Styles.AlertBox>
          <IconWarning color="pendingPure" />
          <Styles.AlertBoxContent>
            <Styles.AlertBoxTitle>{config.alert.title}</Styles.AlertBoxTitle>
            <Styles.AlertBoxMessage>
              {config.alert.message}
              {' '}
              <Link
                href={config.alert.backup.link}
                text={config.alert.backup.text}
                testId="vpsoci-rebuild-backup-link"
                target="_blank"
              />
            </Styles.AlertBoxMessage>
          </Styles.AlertBoxContent>
        </Styles.AlertBox>
      )}
      {(config.agreedTerms && config.agreedTerms.length > 0) && (
        <Styles.InputsWrapper>
          {config.agreedTerms.map(term => (
            <Styles.CheckboxLabel
              key={term.id}
              htmlFor={`checkAgreedTerms-${term.id}`}
              data-testid={`agreedTerms-${term.id}`}
              onClick={(e) => {
                e.preventDefault();
                handleCheckboxChange(term.id);
              }}
            >
              <Checkbox
                checked={checkedTerms.includes(term.id)}
                id={`checkAgreedTerms-${term.id}`}
                onChange={() => handleCheckboxChange(term.id)}
              />
              <Styles.Text>
                {term.text}
              </Styles.Text>
            </Styles.CheckboxLabel>
          ))}
        </Styles.InputsWrapper>
      )}
      <Styles.ModalActions>
        {config.closeButton && (
          <Button
            label={config.closeButton.text}
            variant="tertiary"
            onClick={config.closeButton.action}
            testId={config.closeButton.testId}
            size="large"
            width="full"
          />
        )}
        {config.confirmButton && (
          <Button
            label={config.confirmButton.text}
            variant="primary"
            onClick={config.confirmButton.action}
            testId={config.confirmButton.testId}
            size="large"
            width="full"
            disabled={(config.agreedTerms && (config.agreedTerms.length > 0) && !allTermsAccepted) || config.confirmButton.disabled}
            loading={config.confirmButton.loading}
          />
        )}
      </Styles.ModalActions>
    </Modal>
  );
};

export default VpsStoreModal;
